import { MatSymbol } from 'src/app/shared/icons/models/mat-symbol.interface';

export interface TimelineViewInput {
  title?: string;
  legendItems?: TimelineChartLegendItem[];
  timelineCharts: TimelineChartInputV2[];
  startTime: string;
  endTime: string;
}

export interface TimelineChartInput {
  title?: string;
  legendItems: TimelineChartLegendItem[];
  secondaryTimeline?: TimelineChartSection[];
  mainTimeline: TimelineChart;
  amChart?: AmChartInput;
  startingTime: string;
  endingTime: string;
}

export interface TimelineChartInputV2 {
  symbol?: MatSymbol;
  title?: string;
  legendItems: TimelineChartLegendItem[];
  timelines?: TimelineChart[];
  amChart?: AmChartInput;
  toggleMenu?: TimelineChartToggle[];
  startTime?: string;
  endTime?: string;
}

export type TimelineChartLegendItem =
  TimelineChartBaseLegendItem | TimelineLegendItem | AmChartLegendItem;

export interface TimelineChartBaseLegendItem {
  name: string;
  symbol?: string;
  color?: string;
  summaries?: string[];
}

export interface TimelineLegendItem extends TimelineChartBaseLegendItem {
  sectionKey: string;
  timelineKey?: string;
}

export interface AmChartLegendItem extends TimelineChartBaseLegendItem {
  seriesKey: string;
}

export type LegendItemId =
  { seriesKey: string } | { sectionKey: string; timelineKey?: string };

export interface TimelineChart {
  timelineSections: TimelineChartSection[];
  gap?: number;
  pins?: TimelineChartPin[];
  height?: number;
  key?: string;
  isExcludedFromHighlightFade?: boolean;
  generateFillSections?: FillSectionGeneration;
  startTime?: string;
  endTime?: string;
}

export type FillSectionGeneration =
  | boolean
  | {
      startTime?: string;
      endTime?: string;
    };

export interface TimelineChartSection {
  ratio?: number;
  color?: string;
  startTime?: string;
  endTime?: string;
  key?: string;
  name?: string;
  tooltip?: TimelineChartSectionTooltip | string;
  uuid?: string;
}

export interface TimelineChartSectionTooltip {
  icon: MatSymbol;
  values: string[];
}

export interface TimelineChartPin {
  time: string;
  symbol: string;
  color?: string;
  link?: string;
}

export interface TimelineChartToggle {
  key: string;
  name: string;
  isEnabled: boolean;
  isInsideAmChart?: boolean;
}

export interface AmChartInput {
  data: AmLineChartItem[];
  lines: AmChartLine[];
  mapMarkerSymbol?: MatSymbol;
  seriesType?: XYChartSeriesType; // optional, default is 'line'
}

export type XYChartSeriesType = 'line' | 'smoothed-line' | 'step';

export interface AmChartLine {
  title: string;
  color: string;
  key: string;
  unit?: string;
  disabled?: boolean;
  symbol?: string;
  fillOpacity?: number;
}

export type AmLineChartItem = {
  date: string;
  values: { [key: string]: number };
  position?: {
    lng: number;
    lat: number;
  };
  tooltip?: { [key: string]: string[] };
};

export function isTimelineChartInput(obj: any): obj is TimelineChartInput {
  return (
    typeof obj === 'object' &&
    Array.isArray(obj.legendItems) &&
    typeof obj.startingTime === 'string' &&
    typeof obj.endingTime === 'string' &&
    'mainTimeline' in obj &&
    !('timelines' in obj)
  );
}

export function isTimelineChartInputV2(
  obj: TimelineChartInput | TimelineChartInputV2
): obj is TimelineChartInputV2 {
  return (
    typeof obj === 'object' &&
    Array.isArray(obj.legendItems) &&
    'timelines' in obj &&
    !('mainTimeline' in obj)
  );
}

export function isTimelineLegendItem(
  item: TimelineChartLegendItem
): item is TimelineLegendItem {
  return 'sectionKey' in item;
}

export function isAmChartLegendItem(
  item: TimelineChartLegendItem
): item is AmChartLegendItem {
  return 'seriesKey' in item;
}
